/**
 * Cookie consent — server consent record boundary contract schema (DC-2).
 *
 * Owned here, not beside the route: `apps/api/src/routes/consent/consent.routes.ts` imports this to
 * register the routes, and `apps/landlord` / `apps/tenant` `lib/cookie-consent.api.ts` derive their
 * request and response types from the same objects. See packages/api-types/README.md.
 *
 * Covers (each audience reads and writes only its own authenticated identity's records):
 *   GET /landlord/api/cookie-consent   GET /tenant/api/cookie-consent
 *   PUT /landlord/api/cookie-consent   PUT /tenant/api/cookie-consent
 */
import { z } from 'zod';
import { isoDateTimeString } from '../shared/date.js';
/** An analytics cookie-consent decision. */
export const CookieConsentDecisionSchema = z.enum(['granted', 'denied']);
/** Where the decision was made: the banner, the preferences dialog, or a sign-in reconciliation upload. */
export const CookieConsentSourceSchema = z.enum(['banner', 'preferences', 'sync']);
/**
 * Versions the cookie notice/banner wording. Bumping it re-prompts every visitor: every stored
 * decision then reads as absent (BR-9). Deliberately independent of POLICY_VERSIONS.privacy_policy
 * (@resida/shared) so a policy edit to an unrelated section does not re-prompt cookie choices.
 * Bump only when the cookie notice or Privacy Policy §10 changes materially.
 */
export const COOKIE_NOTICE_VERSION = '2026-10-15';
/** How far in the future a client-claimed decidedAt may be before it is rejected (NFR-SECURITY-2). */
export const COOKIE_CONSENT_MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;
/**
 * PUT body. IP address, user agent and recordedAt are never accepted from the client; unknown
 * fields are stripped. decidedAt is used only to compare recency during reconciliation.
 */
export const PutCookieConsentBodySchema = z
    .object({
    analytics: CookieConsentDecisionSchema,
    noticeVersion: z.string().min(1).max(20),
    source: CookieConsentSourceSchema,
    decidedAt: z.iso.datetime(),
})
    .refine((b) => Date.parse(b.decidedAt) <= Date.now() + COOKIE_CONSENT_MAX_CLOCK_SKEW_MS, {
    message: 'decidedAt is in the future',
    path: ['decidedAt'],
});
/** One stored server consent record. */
export const CookieConsentRecordSchema = z.object({
    analytics: CookieConsentDecisionSchema,
    noticeVersion: z.string(),
    source: CookieConsentSourceSchema,
    decidedAt: isoDateTimeString,
    recordedAt: isoDateTimeString,
});
/** GET and PUT response: the identity's latest record, or null when it has none. */
export const CookieConsentResponseSchema = z.object({
    consent: CookieConsentRecordSchema.nullable(),
});
//# sourceMappingURL=cookie-consent.js.map